import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState("loading"); // loading | ok | error
  const launched = useRef(false);

  useEffect(() => {
    if (launched.current) return; // un seul appel (StrictMode double-invoke)
    launched.current = true;
    const token = params.get("token") || "";
    api.post("/auth/verify-email", { token })
      .then(() => {
        setState("ok");
        if (user) setUser({ ...user, email_verified: true });
      })
      .catch(() => setState("error"));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="confirm-email-page" className="min-h-[70vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO title="Confirmation courriel | Mr Trophée" description="Confirmation de votre adresse courriel." path="/confirmer-courriel" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-6" />
              <p className="text-ivory/70" data-testid="confirm-checking">{t("confirm.checking")}</p>
            </>
          )}
          {state === "ok" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-6" />
              <h1 className="font-serif text-3xl mb-3" data-testid="confirm-success">{t("confirm.success_title")}</h1>
              <p className="text-ivory/60 text-sm mb-8">{t("confirm.success_text")}</p>
              <Link to="/boutique" data-testid="confirm-cta-catalogue"
                className="inline-flex bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
                {t("confirm.cta_catalogue")}
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-6" />
              <h1 className="font-serif text-3xl mb-3" data-testid="confirm-error">{t("confirm.error_title")}</h1>
              <p className="text-ivory/60 text-sm mb-8">{t("confirm.error_text")}</p>
              <Link to="/connexion" data-testid="confirm-cta-login"
                className="inline-flex bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
                {t("confirm.cta_login")}
              </Link>
            </>
          )}
        </div>
      </FadeUp>
    </div>
  );
}
